<script>
	const _ = require('underscore');
	const Grading = require('readworks-grading.js');
	const model = require('model/model');
	const utilities = require('utilities');
	const getWordCount = utilities.getWordCount;
	const { ACTIVE, DONE } = require('types/AssignmentStatus');
	const { WHOLE_CLASS } = require('types/AssignmentTypes');
	const pubsub = require('events/pubsub');

	/**
	 * Returns true if the question set has any written answers.
	 * @param  {Object} qset  The question set object.
	 * @return {boolean} true if it has written answers
	 * @private
	 */
	function _questionSetHasWrittenAnswers(qset) {
		return _.some(qset.questions, function (q) {
			return !Grading.questionTypeIsScorable(q.type);
		});
	}

	// TODO: submit a Jira to change the assessment endpoint to get all data needed, link to WEB-374
	module.exports = {
		props: ['article', 'assessment', 'question-sets', 'ordered-students', 'assignment-id', 'roster'],
		components: {
			'overdue-icon': require('components/overdue-icon.vue').default,
			'dropdown-button': require('components/dropdown-button.vue').default,
			'remove-student-modal': require('components/remove-student-modal.vue').default
		},
		modelData: {
			assessmentExportData: 'exportData'
		},
		data: function () {
			return {
				columnsByStudent: {},
				firstTeacherGradedHrefByQuestionSet: {},
				questionSetHeaders: [],
				students: [],
				studentsWithData: {}
			};
		},
		created() {
			this.updateListingData();
		},
		beforeUpdate() {
			// Purposely aggressively reloading data, in order to pick up new actions by students
			this.updateListingData();
		},
		watch: {
			questionSets: 'updateListingData',
			assessment: 'updateListingData'
		},
		computed: {
			bookOfKnowledge() {
				let assessment = this.assessment;
				let bok = false;

				if (assessment && assessment.options) {
					bok = assessment.options.bok;
				}
				return bok;
			},
			graphicOrganizerAssigned() {
				let assessment = this.assessment;
				let go = null;

				if (assessment && assessment.options) {
					if (assessment.options.wd) {
						go = 'wd';
					} else if (assessment.options.mm) {
						go = 'mm';
					}
				}
				return go;
			},
			studentsSubmitted() {
				let assessment = this.assessment;
				let count = assessment.studentsSubmitted;
				if (assessment.suidsSubmitted) {
					count = Object.keys(assessment.suidsSubmitted).length;
				}
				return count;
			},
			questionsAssignedLookup() {
				const lookup = {};
				this.questionSets.forEach(qset => {
					this.article.getQuestions(qset.id, _.contains(this.assessment.options.expressqs || [], qset.id)).forEach(q => {
						lookup[q.qid] = true;
					});
				});

				return lookup;
			},
			reassignOptions() {
				let options = {};
				let article = this.article;
				let productType = article ? article.productType : null;
				let assessment = this.assessment;
				let closed = assessment ? assessment.closed : null;
				let status = assessment ? assessment.status : null;
				let type = assessment ? assessment.type : null;
				let orderedStudents = this.orderedStudents || [];

				orderedStudents.forEach(s => {
					if (s && s.uid) {
						let sOpts = [];
						if (productType !== 'AAD' && (s.submitted || (closed && !s.edd && s.hasWorkspace))) {
							sOpts.push({
								value: 'reassign',
								text: 'Reassign'
							});
						}
						if (type !== WHOLE_CLASS && orderedStudents.length > 1 && (closed || status === DONE || status === ACTIVE)) {
							sOpts.push({
								value: 'removeStudent',
								text: 'Remove Student'
							});
						}
						options[s.uid] = sOpts;
					}
				});
				return options;
			}
		},
		methods: {
			autoScoredQuestionSetColumn: function (student, answerSet) {
				var col = {
					type: 'badge',
					questions: [],
					class: 'auto-scored'
				};

				Grading.eachQuestionAndSubQuestion(answerSet, q => {
					if (Grading.questionTypeIsScorable(q.questionType) && this.questionsAssignedLookup[q.qid]) {
						col.questions.push(q);
					}
				});

				return col;
			},
			teacherScoredQuestionSetColumn: function (student, answerSet) {
				var col = {
					type: 'badge',
					questions: []
				};

				Grading.eachQuestionAndSubQuestion(answerSet, q => {
					if (!Grading.questionTypeIsScorable(q.questionType) && this.questionsAssignedLookup[q.qid]) {
						col.questions.push(q);
					}
				});

				return col;
			},
			navigateToStudentResult: function (id) {
				pubsub.publish('hide-tooltips');

				if (!this.studentsWithData[id]) {
					model.set({
						student: id,
						assessmentTab: 'assessmentarticle',
						resultsTab: 'studentresults' // prevents ejection from individual results after tab change and pagination
					});
				} else {
					model.set({
						student: id,
						resultsTab: 'studentresults'
					});
				}
			},
			navigateToBokEntries: function (id) {
				model.set({
					student: id,
					assessmentTab: 'bookofknowledge'
				});
			},
			getGraphicOrganizerEntryCount(student) {
				let count = 0;
				if (student && student.graphicOrganizer && student.graphicOrganizer.n) {
					let nodeCount = Object.keys(student.graphicOrganizer.n).length;
					count = nodeCount > 1 ? nodeCount - 1 : 0;
				}
				return count;
			},
			updateListingData: function () {
				var self = this;
				let exportHeader = [];
				let rows = [];
				let exportRowsByUid = {};
				let decodableArticles = new Set();

				if (self.orderedStudents.length === 0 || !self.assessment.students) {
					self.columnsByStudent = {};
					self.studentsWithData = {};
					self.questionSetHeaders = [];
					self.firstTeacherGradedHrefByQuestionSet = {};
					return;
				}

				var studentsWithData = {};

				if (this.article.productType === 'AAD') {
					var wordCounts = {};
					exportHeader = ['Students', 'Articles', 'Decodables Read', 'Words Read', 'Words Written'];

					for (let id in this.article.articles) {
						wordCounts[id] = this.article.articles[id].wordCount;
					}
					if (this.article.supplemental_texts) {
						for (let id in this.article.supplemental_texts.challenge) {
							wordCounts[id] = this.article.supplemental_texts.challenge[id].wordCount;
						}
						for (let id in this.article.supplemental_texts.support) {
							wordCounts[id] = this.article.supplemental_texts.support[id].wordCount;
						}
					}

					if (this.article.dteGroups) {
						for (const dteGroup of this.article.dteGroups) {
							const dteGroupArticles = dteGroup.articles;
							for (const id in dteGroupArticles) {
								decodableArticles.add(id);
								wordCounts[id] = dteGroupArticles[id].wordCount;
							}
						}
					}

					self.orderedStudents.forEach(function (student) {
						let name = student.firstName + (student.lastName ? ' ' + student.lastName : '');
						var data = (studentsWithData[student.uid] = {
							articlesRead: 0,
							wordsRead: 0,
							wordsWritten: 0,
							graphicOrganizerEntries: self.getGraphicOrganizerEntryCount(student),
							decodablesRead: 0
						});

						for (var key in student.aad) {
							if (student.aad[key].s === 's') {
								if (decodableArticles.has(key)) {
									data.decodablesRead++;
								} else {
									data.articlesRead++;
								}
								data.wordsRead += wordCounts[key];
								data.wordsWritten += student.aad[key].b ? getWordCount(student.aad[key].b) : 0;
								data.wordsWritten += student.aad[key].brev ? getWordCount(student.aad[key].brev) : 0;
							}
						}
						rows.push([name, data.articlesRead, data.decodablesRead, data.wordsRead, data.wordsWritten]);
					});

					self.studentsWithData = studentsWithData;
				} else {
					var firstTeacherGradedHrefByQuestionSet = {};
					_.each(self.questionSets, function (qset) {
						firstTeacherGradedHrefByQuestionSet[qset.id] = '#';
						_.each(self.article.questionSets[qset.id].questions, function (q, idx) {
							if (firstTeacherGradedHrefByQuestionSet[qset.id] !== '#') {
								return;
							}

							if (!Grading.questionTypeIsScorable(q.type)) {
								firstTeacherGradedHrefByQuestionSet[qset.id] = {
									qset: qset.id,
									questionId: q.qid
								};
							}
						});
					});

					self.firstTeacherGradedHrefByQuestionSet = firstTeacherGradedHrefByQuestionSet;

					// IE11 does not allow a <template v-for=""> inside of a <tr>, when we do this, the
					// item we are looping over becomes inaccessible. To work around this, we are moving
					// the v-for onto a <th>, but since each question set and student will have 2 <th> or
					// <td> elements, we need to prepare some data here
					var qHeaders = [];
					var now = new Date().getTime() / 1000;
					var showGradeThese = !self.assessment.startDate || self.assessment.startDate <= now;
					_.each(self.questionSets, qset => {
						if (self.assessment.questionSets[qset.id]) {
							qHeaders.push({
								html: 'Multiple Choice',
								class: ''
							});
							exportHeader.push('Multiple Choice');

							qHeaders.push({
								html:
									'<div>Written Answer</div>' +
									(showGradeThese && _questionSetHasWrittenAnswers(self.article.questionSets[qset.id])
										? `<a href="#!qset:${qset.id}/assessmentTab:results/resultsTab:questions/selectedQID:${
												self.firstTeacherGradedHrefByQuestionSet[qset.id].questionId
											}"
									>Grade these</a>`
										: ''),
								class: ''
							});
							exportHeader.push('Written Answer');

							// Loop over the first student's possibly blank answers to get a total question count
							// that includes subQuestons. The students' answers are not sparse because of the
							// addProductInfoToClient method on the classAssessment data mapper in beatrice-data-map.js
							var firstUid = _.keys(self.assessment.students)[0];

							var totQuestions = 0;
							Grading.eachQuestionAndSubQuestion(self.assessment.students[firstUid].answers[qset.id], q => {
								if (this.questionsAssignedLookup[q.qid]) {
									totQuestions++;
								}
							});
							qHeaders.push({
								html: 'Total: <small>' + totQuestions + '</small>',
								class: 'total-column'
							});
							exportHeader.push('Total');
						} else {
							let html = 'Answers Not Available From Students';
							qHeaders.push({
								html,
								colspan: 2,
								class: 'center'
							});
							exportHeader = [html];
						}
					});

					// for assignments with no question sets, add a status column
					if (self.questionSets.length === 0) {
						qHeaders.push({
							html: 'Status',
							class: 'total-column'
						});
						exportHeader.push('Status');
					}

					self.questionSetHeaders = qHeaders;

					var columnsByStudent = {};
					_.each(self.assessment.students, (student, uid) => {
						if (!uid || uid === 'null') {
							return;
						}

						let status = null;
						if (student.submitted) {
							status = 'Submitted';
							if (student.overdue) {
								status += ' Late';
							}
						} else {
							status = 'Not Submitted';
						}

						let name = student.firstName + (student.lastName ? ' ' + student.lastName : '');

						var cols = (columnsByStudent[uid] = []);

						if (self.questionSets.length === 0) {
							cols.push({
								type: 'html',
								html: student.submitted ? 'Submitted' : 'Not Submitted',
								class: 'total-column'
							});
							rows.push([name, status]);
						} else {
							_.each(self.questionSets, qset => {
								if (self.assessment.students[uid].answers) {
									Grading.eachQuestionAndSubQuestion(self.assessment.students[uid].answers[qset.id], function (q) {
										if (student.submitted || q.answered || q.teacherNote || (student.generalNotes && student.generalNotes[qset.id])) {
											studentsWithData[uid] = 1;
										}
									});
								}

								let answerSet = student.answers[qset.id];
								let totalHTML = null;
								let needsGrading = false;
								let total = 0;
								let totalRight = 0;
								let totalPercent = null;
								let multipleChoiceScores = [];
								let multipleChoicePercent = null;
								let writtenAnswerScores = [];
								let writtenAnswerPercent = null;

								Grading.eachQuestionAndSubQuestion(answerSet, q => {
									if (this.questionsAssignedLookup[q.qid]) {
										total++;
									}
									if (Grading.questionTypeIsScorable(q.questionType) && this.questionsAssignedLookup[q.qid]) {
										multipleChoiceScores.push(q.score);
									}
									if (!Grading.questionTypeIsScorable(q.questionType) && this.questionsAssignedLookup[q.qid]) {
										writtenAnswerScores.push(q.score);
										if (typeof q.score !== 'number') {
											needsGrading = true;
										}
									}
									if (typeof q.score === 'number') {
										totalRight += q.score / 100;
									}
								});

								if (multipleChoiceScores.length) {
									let mcScore = multipleChoiceScores.reduce((acc, s) => {
										if (typeof s === 'number') {
											return acc + s;
										} else {
											return acc;
										}
									}, 0);
									multipleChoicePercent = Math.round(mcScore / multipleChoiceScores.length) + '%';
								} else {
									multipleChoicePercent = 'N/A';
								}

								if (writtenAnswerScores.length) {
									if (needsGrading) {
										writtenAnswerPercent = 'Needs grading';
									} else {
										let waScore = writtenAnswerScores.reduce((acc, s) => {
											return acc + s;
										}, 0);
										writtenAnswerPercent = Math.round(waScore / writtenAnswerScores.length) + '%';
									}
								} else {
									writtenAnswerPercent = 'N/A';
								}

								if (!student.submitted && !self.assessment.closed) {
									totalHTML = 'Not submitted';
									totalPercent = '';
								} else if (needsGrading && !self.assessment.closed) {
									totalHTML = 'Needs grading';
									totalPercent = '';
								} else {
									totalPercent = Math.round((totalRight / total) * 100) + '%';
									totalHTML = totalPercent + '<small>' + totalRight + '</small>';
								}

								exportRowsByUid[uid] = [name, status, multipleChoicePercent, writtenAnswerPercent, totalPercent];

								cols.push(self.autoScoredQuestionSetColumn(student, answerSet));
								cols.push(self.teacherScoredQuestionSetColumn(student, answerSet));
								cols.push({
									type: 'html',
									html: totalHTML,
									class: 'total-column'
								});
							});
						}
					});

					self.columnsByStudent = columnsByStudent;
					self.studentsWithData = studentsWithData;
					let studentStatusHeader = ['Students', 'Status'];
					if (self.questionSets.length === 0) {
						exportHeader = studentStatusHeader;
					} else {
						exportHeader = studentStatusHeader.concat(exportHeader);
					}
				}

				this.orderedStudents.forEach(s => {
					if (exportRowsByUid[s.uid]) {
						rows.push(exportRowsByUid[s.uid]);
					}
				});

				this.exportData = [exportHeader].concat(rows);
			},
			onReassignOptionClick(option, student) {
				if (option.value === 'reassign') {
					utilities.openModal('#modal-reassign', {
						student: student,
						assessment: this.assessment,
						assignmentId: this.assignmentId,
						roster: this.roster
					});
				} else if (option.value === 'removeStudent') {
					utilities.openModal('#modal-remove-student', {
						suid: student.uid,
						assignment: this.assessment,
						roster: this.roster
					});
				}
				// else if (option.value === 'markdone') {
				// 	utilities.openModal('#modal-mark-done', {
				// 		student: student,
				// 		assessment: this.assessment,
				// 		assignmentId: this.assignmentId,
				// 		questionsAssignedLookup: this.questionsAssignedLookup
				// 		roster: this.roster
				// 	});
				// }
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';

	.class-results {
		td.no-data-submitted:after,
		td.no-data:after {
			text-align: center;
			width: 100%;
			display: inline-block;
			color: #dcdee0;
		}

		td.no-data:after {
			content: 'student not started';
		}

		td.no-data-submitted:after {
			content: 'blank submission';
		}

		table.results {
			th {
				width: auto;
			}

			.student-name-container {
				display: flex;
				align-items: center;

				.student-name {
					flex: 1;
				}

				.dropdown-button {
					margin-left: 0.5rem;
				}

				.overdue-icon {
					padding: 4px;
				}

				.reassign-link {
					margin-left: 1rem;
					color: $c-slate-dark;
					font-size: 90%;

					&:hover {
						color: $c-blue;
					}
				}
			}
		}
	}

	@media print {
		section.class-results {
			width: 100% !important;
			max-width: 100%;
			margin-left: 0rem !important;
			margin-right: 0rem !important;

			> section {
				margin-top: 0rem !important;
				margin-bottom: 0rem !important;
			}

			.print-header {
				display: flex;

				h2.print-header-title {
					flex: 1;
					margin: 0rem;
				}
			}
		}
	}
</style>
<template>
	<section class="wrapper class-results">
		<h1
			v-if="questionSets.length === 0 && article.productType !== 'AAD'"
			class="empty-qset-header">
			Class Results: No questions have been assigned
		</h1>
		<section>
			<div class="print-header print-only">
				<h2 class="print-header-title">{{ article.title }}</h2>
				<div class="print-header-dates">
					<div>
						Start Date:
						<time>{{ (assessment.startDate || assessment.creationTimestamp) | date }}</time>
					</div>
					<div v-if="assessment.deadline">
						{{ article.productType === 'AAD' ? 'End Date' : 'Due Date' }}:
						<time>{{ assessment.deadline | date }}</time>
					</div>
				</div>
			</div>
			<table
				v-if="article.productType !== 'AAD'"
				class="results">
				<thead>
					<tr>
						<th>Students</th>
						<th
							colspan="3"
							v-for="qset in questionSets"
							:key="qset.title">
							{{ qset.title }}
						</th>
						<th v-if="questionSets.length === 0">&nbsp;</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<th class="submitted">{{ studentsSubmitted }}/{{ assessment.studentCount }} Submitted</th>
						<th
							v-for="qh in questionSetHeaders"
							:key="qh.class"
							:class="qh.class"
							:colspan="qh.colspan"
							v-html="qh.html"></th>
					</tr>
					<tr
						v-for="student in orderedStudents"
						:key="student.uid">
						<td>
							<div class="student-name-container">
								<a
									v-if="student.hasWorkspace"
									class="student-name print-visible"
									href="javascript:void(0)"
									data-tab="studentresults"
									v-on:click.prevent="navigateToStudentResult(student.uid)"
									v-tooltip="'View student results'">
									{{ student.firstName }} {{ student.lastName }}
								</a>
								<span
									v-else
									class="student-name">
									{{ student.firstName }} {{ student.lastName }}
								</span>
								<overdue-icon v-if="student.overdue"></overdue-icon>
								<dropdown-button
									v-if="article.productType !== 'AAD' && reassignOptions && reassignOptions[student.uid] && reassignOptions[student.uid].length"
									class="reassign-options print-hidden"
									@click-item="onReassignOptionClick"
									:items="reassignOptions[student.uid]"
									:data-item="student"
									:icon-cls="'cog fa-solid'">
								</dropdown-button>
							</div>
						</td>
						<td
							v-for="sc in columnsByStudent[student.uid]"
							:key="sc.html"
							:class="sc.class">
							<span
								v-if="sc.type === 'html'"
								v-html="sc.html">
							</span>
							<template v-if="sc.type === 'badge'">
								<grade-badge
									:closed="assessment.closed"
									:questions="sc.questions"
									:student="student"></grade-badge>
							</template>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				class="row"
				v-if="article.productType === 'AAD'">
				<div class="columns large-8">
					<table class="results">
						<thead>
							<tr>
								<th>Students</th>
								<th>Articles</th>
								<th v-if="assessment.options.decodables">Decodables Read</th>
								<th>Words Read</th>
								<th v-if="bookOfKnowledge">Words Written</th>
								<th v-if="graphicOrganizerAssigned">Words Explored</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="student in orderedStudents"
								:key="student.uid">
								<td>
									<a
										v-if="studentsWithData[student.uid] && (studentsWithData[student.uid].articlesRead > 0 || studentsWithData[student.uid].decodablesRead > 0)"
										class="student-name print-visible"
										href="javascript:void(0)"
										data-tab="bokentries"
										v-on:click.prevent="navigateToBokEntries(student.uid)"
										v-tooltip="'View student results'"
										>{{ student.firstName }} {{ student.lastName }}</a
									>
									<template v-else> {{ student.firstName }} {{ student.lastName }} </template>
								</td>
								<td class="center">{{ studentsWithData[student.uid] && studentsWithData[student.uid].articlesRead }}</td>
								<td
									class="center"
									v-if="assessment.options.decodables">
									{{ studentsWithData[student.uid] && studentsWithData[student.uid].decodablesRead }}
								</td>
								<td class="center">{{ studentsWithData[student.uid] && studentsWithData[student.uid].wordsRead | commaGrouping }}</td>
								<td
									class="center"
									v-if="bookOfKnowledge">
									{{ studentsWithData[student.uid] && studentsWithData[student.uid].wordsWritten | commaGrouping }}
								</td>
								<td
									class="center"
									v-if="graphicOrganizerAssigned">
									{{ studentsWithData[student.uid] && studentsWithData[student.uid].graphicOrganizerEntries }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</section>
		<remove-student-modal></remove-student-modal>
	</section>
</template>
